import { pdfjs } from "react-pdf";
import { Document, Page } from "react-pdf";
import { useEffect, useState, useRef } from "react";
import {
    Grid,
    IconButton,
    Typography,
    Tooltip,
    useMediaQuery,
    useTheme,
    Divider,
    Snackbar,
    Alert,
    CircularProgress,
    Box,
    Slide,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import SaveAltTwoToneIcon from '@mui/icons-material/SaveAltTwoTone';
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import FileOpenIcon from '@mui/icons-material/FileOpen';
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

pdfjs.GlobalWorkerOptions.workerSrc = "/workers/pdf.worker.min.mjs";

const PdfViewer = ({ pdffile }) => {
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.down("sm"));
    const containerRef = useRef();
    const [numPages, setNumPages] = useState(null);
    const [pageLoading, setPageLoading] = useState(true);
    const [scale, setScale] = useState(1);
    const [containerWidth, setContainerWidth] = useState(600);
    const [pageNum, setPageNum] = useState(1);
    const [slideDirection, setSlideDirection] = useState("left");
    const [workerFailed, setWorkerFailed] = useState(false);
    const [hasPromiseError, setHasPromiseError] = useState(false);
    const lref = useRef();

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });

    useEffect(() => {
        if (typeof Promise.withResolvers !== "function") {
            console.error("Promise.withResolvers is not supported in this environment.");
            setHasPromiseError(true);
        }
    }, []);

    useEffect(() => {
        if (containerRef.current) {
            setContainerWidth(containerRef.current.offsetWidth);
        }
    }, [matches]);

    const showSnackbar = (message, severity = "info") => {
        setSnackbar({ open: true, message, severity });
    };

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
        setPageLoading(false);
    };

    const onDocumentLoadError = (error) => {
        console.error("Error loading PDF:", error);
        setPageLoading(false);
        setWorkerFailed(true);
        // showSnackbar("Failed to load PDF", "error");
    };

    const goToPreviousPage = () => {
        if (pageNum > 1) {
            setSlideDirection("right");
            setPageNum((prev) => prev - 1);
        }
    };

    const goToNextPage = () => {
        if (pageNum < numPages) {
            setSlideDirection("left");
            setPageNum((prev) => prev + 1);
        }
    };

    const zoomIn = () => {
        setScale((prev) => Math.min(prev + 0.2, 3));
        setPageLoading(true);
    };

    const zoomOut = () => {
        setScale((prev) => Math.max(prev - 0.2, 0.3));
        setPageLoading(true);
    };

    const onClick = async () => {
        showSnackbar("Download started...", "info");

        try {
            const response = await fetch(pdffile.surl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = pdffile.surl.split("/").pop();
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

            showSnackbar("Download successful!", "success");
        } catch (error) {
            console.error("Download failed", error);
            showSnackbar("Download failed. Please try again.", "error");
        }
    };

    const onClickOpen = () => {
        lref.current.click();
    };

    const buttonBaseStyle = {
        fontSize: '0.75rem',
        px: 1.5,
        py: 0.5,
        width: 100,
        borderRadius: 2,
        color: 'white',
        minHeight: 32,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    };

    const openButtonStyle = {
        ...buttonBaseStyle,
        backgroundColor: '#9c27b0',
        '&:hover': {
            backgroundColor: '#7b1fa2',
        },
    };

    const downloadButtonStyle = {
        ...buttonBaseStyle,
        backgroundColor: 'primary.main',
        '&:hover': {
            backgroundColor: 'primary.dark',
        },
    };

    return (
        <Grid container spacing={2} direction="column" alignItems="center">
            <Grid
                item
                style={{
                    maxWidth: matches ? "350px" : "600px",
                    overflowX: "auto",
                    textAlign: "center",
                }}
            >
                {workerFailed || hasPromiseError ? (
                    <Grid>
                        <Tooltip title={"Download"} arrow>
                            <IconButton
                                style={{
                                    border: "1px solid #ccc",
                                    borderRadius: "8px",
                                    boxShadow: "1px 1px 3px #bebebe",
                                }}
                                onClick={onClick}
                            >
                                <InsertDriveFileIcon color="error" />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                ) : (
                    <div>
                        <Grid
                            item
                            ref={containerRef}
                            sx={{
                                width: matches ? "350px" : "550px",
                                overflow: "auto",
                            }}
                        >
                            <Document
                                file={pdffile.surl}
                                onLoadSuccess={onDocumentLoadSuccess}
                                onLoadError={onDocumentLoadError}
                                loading={
                                    <Box height={400} display="flex" alignItems="center" justifyContent="center">
                                        <CircularProgress />
                                    </Box>
                                }
                            >
                                <Box position="relative" width="100%">
                                    {pageLoading && (
                                        <Box
                                            position="absolute"
                                            top={0}
                                            left={0}
                                            right={0}
                                            bottom={0}
                                            zIndex={1}
                                            bgcolor="rgba(255,255,255,0.6)"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <CircularProgress />
                                        </Box>
                                    )}

                                    <Slide
                                        direction={slideDirection}
                                        in={!pageLoading}
                                        mountOnEnter
                                        unmountOnExit
                                        timeout={400}
                                        key={pageNum}
                                    >
                                        <Box position="relative">
                                            <IconButton
                                                onClick={goToPreviousPage}
                                                disabled={pageNum <= 1}
                                                sx={{
                                                    position: "absolute",
                                                    top: "50%",
                                                    left: 8,
                                                    transform: "translateY(-50%)",
                                                    bgcolor: "white",
                                                    boxShadow: 1,
                                                    zIndex: 10,
                                                }}
                                            >
                                                <ArrowBackIosIcon color="primary" />
                                            </IconButton>

                                            <Page
                                                pageNumber={pageNum}
                                                width={containerWidth}
                                                scale={scale}
                                                renderTextLayer={false}
                                                renderAnnotationLayer={false}
                                                onRenderSuccess={() => setPageLoading(false)}
                                                onRenderError={() => setPageLoading(false)}
                                            />

                                            <IconButton
                                                onClick={goToNextPage}
                                                disabled={pageNum >= numPages}
                                                sx={{
                                                    position: "absolute",
                                                    top: "50%",
                                                    right: 8,
                                                    transform: "translateY(-50%)",
                                                    bgcolor: "white",
                                                    boxShadow: 1,
                                                    zIndex: 10,
                                                }}
                                            >
                                                <ArrowForwardIosIcon color="primary" />
                                            </IconButton>

                                            {pageNum < numPages && (
                                                <Box sx={{ display: "none" }}>
                                                    <Page
                                                        pageNumber={pageNum + 1}
                                                        width={containerWidth}
                                                        scale={scale}
                                                        renderTextLayer={false}
                                                        renderAnnotationLayer={false}
                                                    />
                                                </Box>
                                            )}
                                            {pageNum > 1 && (
                                                <Box sx={{ display: "none" }}>
                                                    <Page
                                                        pageNumber={pageNum - 1}
                                                        width={containerWidth}
                                                        scale={scale}
                                                        renderTextLayer={false}
                                                        renderAnnotationLayer={false}
                                                    />
                                                </Box>
                                            )}
                                        </Box>
                                    </Slide>
                                </Box>
                            </Document>
                        </Grid>

                        <Grid
                            item
                            container
                            direction="row"
                            alignItems="center"
                            justifyContent="center"
                            spacing={2}
                        >
                            <Grid item>
                                <Tooltip title="Open" arrow>
                                    <IconButton size="small" onClick={onClickOpen} sx={openButtonStyle}>
                                        <FileOpenIcon fontSize="small" sx={{ marginRight: 1 }} />
                                        Open
                                    </IconButton>
                                </Tooltip>
                            </Grid>

                            <a
                                ref={lref}
                                style={{ display: 'none' }}
                                href={pdffile.surl}
                                target="_blank"
                                rel="noreferrer"
                            />

                            <Grid item>
                                <Tooltip title="Download" arrow>
                                    <IconButton size="small" onClick={onClick} sx={downloadButtonStyle}>
                                        <SaveAltTwoToneIcon fontSize="small" sx={{ marginRight: 0.3 }} />
                                        Download
                                    </IconButton>
                                </Tooltip>
                            </Grid>

                            {/* <Grid item>
                    <Tooltip title="Zoom In" arrow>
                        <IconButton onClick={zoomIn} color="primary" disabled={scale >= 3}>
                            <ZoomInIcon />
                        </IconButton>
                    </Tooltip>
                </Grid>

                <Grid item>
                    <Tooltip title="Zoom Out" arrow>
                        <IconButton onClick={zoomOut} color="primary" disabled={scale <= 0.3}>
                            <ZoomOutIcon />
                        </IconButton>
                    </Tooltip>
                </Grid> */}
                        </Grid>
                    </div>
                )}
            </Grid>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Grid>
    );
};

export default PdfViewer;